/**
 * courier_window.ts — the COURIER CONTRACT window (space campaign J, 2026-09-30; report §17.2): every term named before
 * you commit (lib/courier_terms.ts), then the one action that fits — Accept (a courier, docked at the origin), Deliver
 * (the courier, docked at the destination), Cancel (the issuer, while unaccepted). Accept lives HERE — the Haulage row
 * opens the terms first. A refusal is printed and toasted in the server's own words. Tokens only; 720p at 200 %.
 */
import { makeWindow, type UiWindow } from './ui_window.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { courierTerms, type CourierTermsIn, type TermsLine } from './courier_terms.js';
import { insuranceQuote } from '@shared/space_catalog.js';
import { CHASSIS } from './fleet.js';
import { HOLD_UNITS_PER_CARGO } from '@shared/space_industry.js';
import { stationLabel } from './market_panel.js';

interface Id { isEqual(o: unknown): boolean; toHexString(): string }
interface Row { contractId: bigint; issuer: Id; courier: Id; originKey: string; destKey: string; cargoJson: string; rewardSov: bigint; collateralSov: bigint; state: string; expiresAt: { microsSinceUnixEpoch: bigint } }
const css = {
  row: 'display:grid;grid-template-columns:minmax(110px, 28%) 1fr;gap:4px 10px;align-items:baseline',
  label: 'font:600 11px var(--ax-mono);color:var(--ax-muted)',
  text: 'font:12px var(--ax-font);color:var(--ax-text);line-height:1.35',
  warn: 'font:12px var(--ax-font);color:var(--ax-warn);line-height:1.35',
  head: 'font:700 11px var(--ax-mono);color:var(--ax-hi);letter-spacing:1px;margin-top:6px',
  btn: 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:5px 12px;cursor:pointer;font:12px var(--ax-mono);min-height:30px',
  primary: 'background:var(--ax-accent);border:1px solid var(--ax-accent);border-radius:var(--ax-radius);color:var(--ax-on-accent);padding:5px 14px;cursor:pointer;font:700 12px var(--ax-mono);min-height:30px',
};
type Db = {
  courierContract?: { iter(): Iterable<Row> };
  account?: { iter(): Iterable<{ identity: Id; username: string }> };
  playerShip?: { iter(): Iterable<{ shipId: bigint; owner: Id; chassisKey: string; name: string; active: boolean }> };
  shipComponent?: { iter(): Iterable<{ shipId: bigint; slot: string; quality: number }> };
  cargoLot?: { iter(): Iterable<{ locId: string; qty: number }> };
  shipInsurance?: { iter(): Iterable<{ shipId: bigint; payoutSov: bigint; expiresAt: { microsSinceUnixEpoch: bigint } }> };
  spaceLocation?: { iter(): Iterable<{ identity: Id; mode: string; stationKey: string }> };
};
const safe = <T>(f: () => T, fb: T): T => { try { return f(); } catch { return fb; } };

/** Build the terms input from the live rows (exported for the bed and the harness). */
export function termsInputFor(r: Row, nowMs = Date.now()): CourierTermsIn {
  const conn = atlas.conn;
  const db = conn ? (conn.db as unknown as Db) : ({} as Db);
  const me = conn?.identity as unknown as Id | undefined;
  const nameOf = (id: Id): string => safe(() => { for (const a of db.account?.iter() ?? []) if (a.identity.isEqual(id)) return a.username; return ''; }, '') || (me && id.isEqual(me) ? 'you' : id.toHexString().slice(0, 8));
  let cargo: Array<{ familyKey: string; qty: number }> = [];
  try { cargo = JSON.parse(r.cargoJson) as Array<{ familyKey: string; qty: number }>; } catch { /* */ }
  const role: CourierTermsIn['role'] = me && r.issuer.isEqual(me) ? 'issuer' : me && r.state === 'accepted' && r.courier.isEqual(me) ? 'courier' : 'other';
  let activeHull: CourierTermsIn['activeHull'] = null;
  const ship = safe(() => [...(db.playerShip?.iter() ?? [])].find((s) => me && s.owner.isEqual(me) && s.active) ?? null, null);
  if (ship) {
    let q = -1, used = 0;
    safe(() => { for (const c of db.shipComponent?.iter() ?? []) if (c.shipId === ship.shipId && c.slot === 'cargo') q = c.quality; return 0; }, 0);
    safe(() => { for (const l of db.cargoLot?.iter() ?? []) if (l.locId === `ship:${ship.shipId}`) used += l.qty; return 0; }, 0);
    const cap = ((CHASSIS[ship.chassisKey]?.cargo ?? 4) + (q >= 0 ? Math.round(Math.max(0, Math.min(1000, q)) / 1000 * 20) : 0)) * HOLD_UNITS_PER_CARGO;
    const pol = safe(() => [...(db.shipInsurance?.iter() ?? [])].find((p) => p.shipId === ship.shipId && Number(p.expiresAt.microsSinceUnixEpoch / 1000n) > nowMs) ?? null, null);
    activeHull = { name: ship.name, free: Math.max(0, cap - used), insuredPayout: pol ? pol.payoutSov : null, insurable: insuranceQuote(BigInt(CHASSIS[ship.chassisKey]?.costSov ?? 0)).insurable };
  }
  return {
    contractId: r.contractId, originName: stationLabel(r.originKey), destName: stationLabel(r.destKey), cargo,
    rewardSov: r.rewardSov, collateralSov: r.collateralSov, state: r.state,
    expiresAtMs: Number(r.expiresAt.microsSinceUnixEpoch / 1000n), nowMs, role,
    issuerName: nameOf(r.issuer), courierName: r.state === 'accepted' ? nameOf(r.courier) : '—', activeHull,
  };
}

class CourierWindow {
  private win: UiWindow | null = null;
  private contractId = 0n;
  private status = '';
  private timer = 0;

  open(contractId: bigint): void {
    if (contractId !== this.contractId) this.status = '';
    this.contractId = contractId;
    if (!this.win) {
      this.win = makeWindow({ id: 'courier-terms', title: 'Courier contract', icon: 'menu', width: 600, minWidth: 320, minHeight: 200, onClose: () => window.clearInterval(this.timer) });
      this.win.root.dataset.courierWindow = '1';
    }
    this.win.root.style.maxWidth = 'calc(100vw - 16px)';
    this.win.root.style.maxHeight = 'calc(100vh - 16px)';
    this.win.setOpen(true);
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.render();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => this.render(), 1000);
  }
  close(): void { this.win?.setOpen(false); window.clearInterval(this.timer); }
  isOpen(): boolean { return !!this.win?.isOpen(); }

  private row(): Row | null {
    const db = (atlas.conn ? (atlas.conn.db as unknown as Db) : ({} as Db));
    return safe(() => [...(db.courierContract?.iter() ?? [])].find((c) => c.contractId === this.contractId) ?? null, null);
  }
  private dockedAt(): string {
    const db = (atlas.conn ? (atlas.conn.db as unknown as Db) : ({} as Db));
    const me = atlas.conn?.identity as unknown as Id | undefined;
    return safe(() => { for (const l of db.spaceLocation?.iter() ?? []) if (me && l.identity.isEqual(me) && l.mode === 'docked') return l.stationKey; return ''; }, '');
  }
  private async call(p: Promise<void> | undefined, ok: string): Promise<void> {
    if (!p) { this.say('Not connected', 'error'); return; }
    try { await p; this.say(ok, 'success'); } catch (e) { this.say(String((e as Error)?.message ?? e), 'error'); }
  }
  private say(text: string, kind: 'error' | 'info' | 'success'): void {
    this.status = text;
    bus.emit('atlas:toast', { text, kind } as never);
    this.render();
  }
  private render(): void {
    if (!this.win) return;
    const body = this.win.body;
    body.replaceChildren();
    body.style.cssText = 'display:flex;flex-direction:column;gap:6px;padding:10px 12px;overflow-y:auto;box-sizing:border-box';
    const r = this.row();
    if (!r) { const d = document.createElement('div'); d.style.cssText = css.text; d.textContent = 'That contract is gone.'; body.appendChild(d); return; }
    const input = termsInputFor(r);
    this.win.setTitle(`Courier contract #${r.contractId} — the terms`);
    const head = document.createElement('div'); head.style.cssText = css.head;
    head.textContent = r.state === 'offered' ? 'READ BEFORE YOU ACCEPT' : `THIS CONTRACT IS ${r.state.toUpperCase()}`;
    body.appendChild(head);
    this.lines(body, courierTerms(input));
    const acts = document.createElement('div'); acts.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin-top:6px';
    const here = this.dockedAt();
    const R = (atlas.conn?.reducers ?? {}) as unknown as Record<string, (a: { contractId: bigint }) => Promise<void>>;
    if (r.state === 'offered' && input.role === 'other') {
      const atOrigin = here === r.originKey;
      acts.appendChild(this.button(atOrigin ? '✔ Accept these terms' : `Accept at ${input.originName} (dock there)`, atOrigin, true, () => void this.call(R.acceptCourierContract?.({ contractId: r.contractId }), 'Accepted — the crate is aboard your active hull'), 'accept'));
    }
    if (r.state === 'offered' && input.role === 'issuer') acts.appendChild(this.button('✕ Cancel the contract', true, false, () => void this.call(R.cancelCourierContract?.({ contractId: r.contractId }), 'Cancelled — the manifest is back in your warehouse'), 'cancel'));
    if (r.state === 'accepted' && input.role === 'courier') {
      const atDest = here === r.destKey;
      acts.appendChild(this.button(atDest ? '⬇ Deliver' : `Deliver at ${input.destName}`, atDest, true, () => void this.call(R.deliverCourierContract?.({ contractId: r.contractId }), 'Delivered — reward and collateral paid'), 'deliver'));
    }
    acts.appendChild(this.button('Close', true, false, () => this.close(), 'close'));
    body.appendChild(acts);
    if (this.status) { const s = document.createElement('div'); s.dataset.courierStatus = '1'; s.style.cssText = 'font:12px var(--ax-font);color:var(--ax-soft)'; s.textContent = this.status; body.appendChild(s); }
  }
  private lines(body: HTMLElement, lines: readonly TermsLine[]): void {
    for (const l of lines) {
      const r = document.createElement('div');
      r.style.cssText = css.row;
      r.dataset.term = l.label;
      const a = document.createElement('div'); a.style.cssText = css.label; a.textContent = l.label;
      const b = document.createElement('div'); b.style.cssText = l.warn ? css.warn : css.text; b.textContent = (l.warn ? '⚠ ' : '') + l.text;
      r.append(a, b);
      body.appendChild(r);
    }
  }
  private button(text: string, enabled: boolean, primary: boolean, onClick: () => void, data: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = text;
    b.disabled = !enabled;
    b.style.cssText = (primary ? css.primary : css.btn) + (enabled ? '' : ';opacity:.45;cursor:default');
    b.dataset.courierAct = data;
    b.onclick = () => { if (enabled) onClick(); };
    return b;
  }
}

let inst: CourierWindow | null = null;
export function courierWindow(): CourierWindow { return (inst ??= new CourierWindow()); }
